import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/StoreDataContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { resolveImageUrl } from "../lib/images";
import { toast } from "sonner";

export default function Checkout() {
    const { items, total, clear } = useCart();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        address: "",
        city: "",
        notes: "",
        payment_method: "Transferencia bancaria",
    });

    if (items.length === 0) {
        return (
            <div className="text-center pt-40 pb-20">
                <p className="font-serif text-3xl">No hay productos para procesar.</p>
                <Link to="/catalogo" className="text-rose underline mt-4 inline-block">
                    Ir al catálogo
                </Link>
            </div>
        );
    }

    function handleChange(e) {
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                items: items.map((it) => ({
                    product_id: it.product_id,
                    name: it.name,
                    price: it.price,
                    quantity: it.quantity,
                    image: it.image,
                })),
            };
            const { data } = await api.post("/orders", payload);
            clear();
            navigate(`/pedido-confirmado/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    const shipping = total >= (settings?.free_shipping_threshold ?? 50) ? 0 : 5;

    return (
        <div className="px-6 md:px-12 lg:px-20 pt-32 pb-20" data-testid="checkout-page">
            <h1 className="font-serif text-5xl sm:text-6xl text-charcoal mb-12">
                Finalizar <span className="italic">pedido.</span>
            </h1>
            <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 lg:grid-cols-3 gap-12"
                data-testid="checkout-form"
            >
                <div className="lg:col-span-2 space-y-6">
                    <p className="overline">Datos de envío</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field
                            label="Nombre completo"
                            name="customer_name"
                            value={form.customer_name}
                            onChange={handleChange}
                            required
                            testid="checkout-name"
                        />
                        <Field
                            label="Correo electrónico"
                            name="customer_email"
                            type="email"
                            value={form.customer_email}
                            onChange={handleChange}
                            required
                            testid="checkout-email"
                        />
                        <Field
                            label="Teléfono / WhatsApp"
                            name="customer_phone"
                            value={form.customer_phone}
                            onChange={handleChange}
                            required
                            testid="checkout-phone"
                        />
                        <Field
                            label="Ciudad"
                            name="city"
                            value={form.city}
                            onChange={handleChange}
                            required
                            testid="checkout-city"
                        />
                        <div className="md:col-span-2">
                            <Field
                                label="Dirección"
                                name="address"
                                value={form.address}
                                onChange={handleChange}
                                required
                                testid="checkout-address"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline block mb-2">Método de pago</label>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                {["Transferencia bancaria", "Contra entrega", "Nequi / Daviplata"].map((m) => (
                                    <label
                                        key={m}
                                        className={`border rounded-lg px-4 py-3 text-sm cursor-pointer transition ${
                                            form.payment_method === m
                                                ? "border-gold bg-blush"
                                                : "border-border-soft hover:border-gold"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            value={m}
                                            checked={form.payment_method === m}
                                            onChange={handleChange}
                                            className="hidden"
                                            data-testid={`payment-${m.replace(/\s+/g, '-').toLowerCase()}`}
                                        />
                                        {m}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline block mb-2">Notas (opcional)</label>
                            <textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleChange}
                                rows={3}
                                className="w-full bg-white border border-border-soft rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose focus:border-transparent"
                                data-testid="checkout-notes"
                            />
                        </div>
                    </div>

                    <div className="bg-blush rounded-xl p-5 text-sm text-taupe">
                        Tu pedido se registrará y nos contactaremos contigo para coordinar el
                        pago y el envío. El pago se procesa al confirmar el envío (no hay cobro online).
                    </div>
                </div>

                <div className="lg:col-span-1">
                    <div className="bg-white rounded-xl border border-border-soft/40 p-6 lg:sticky lg:top-28">
                        <h2 className="font-serif text-2xl text-charcoal">Tu pedido</h2>
                        <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
                            {items.map((it) => (
                                <div key={it.product_id} className="flex gap-3 text-sm">
                                    <div className="w-12 h-12 rounded bg-blush overflow-hidden shrink-0">
                                        {it.image && (
                                            <img src={resolveImageUrl(it.image)} alt="" className="w-full h-full object-cover" />
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-serif text-base">{it.name}</p>
                                        <p className="text-taupe text-xs">
                                            {it.quantity} × ${it.price.toFixed(2)}
                                        </p>
                                    </div>
                                    <p className="font-serif">${(it.price * it.quantity).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                        <div className="border-t border-border-soft mt-4 pt-4 space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-taupe">Subtotal</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-taupe">Envío</span>
                                <span>{shipping === 0 ? "Gratis" : `$${shipping.toFixed(2)}`}</span>
                            </div>
                            <div className="flex justify-between font-serif text-xl pt-2 border-t border-border-soft">
                                <span>Total</span>
                                <span data-testid="checkout-total">${(total + shipping).toFixed(2)}</span>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="mt-6 w-full bg-rose text-white hover:bg-rose-dark rounded-full px-8 py-4 transition-colors text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                            data-testid="submit-order-btn"
                        >
                            {submitting ? "Procesando..." : "Confirmar pedido"}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function Field({ label, name, value, onChange, type = "text", required, testid }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            <input
                name={name}
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                className="w-full bg-white border border-border-soft rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose focus:border-transparent"
                data-testid={testid}
            />
        </div>
    );
}
